import { useAiWriter } from '@/hooks/useAiWriter'
import { createDraft, getSearchData } from '@/services/apis/aiwriter'
import { DataEvent, OperateType } from '@/types/aiAsk'
import { IAIWritePrecondition, IOutline, IRequestStatus } from '@/types/outline'
import { EE } from '@/utils/aievent'
import { chalkConsole, formatTime, replaceCitation, sleep } from '@/utils/outline'
import bluebird from 'bluebird'
import chalk from 'chalk'
import { cloneDeep } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { history, useModel } from 'umi'
import { v4 as uuidV4 } from 'uuid'

// AI大纲
const useAiOutline = () => {
  const { methods } = useModel('editor')
  const { handleInitStatus } = useModel('domCheck')

  const [isAIWriting, setIsAIWriting] = useState(false) // 是否在AI写作状态(从开始获取大纲，到文章生成结束)
  const [inAIOutlineProcess, setInAIOutlineProcess] = useState(true) // 是否处于AI大纲的的写作流程（从大纲获取开始，到进入编辑器状态结束（获取文章开始就进入编辑器））
  const [precondition, setPrecondition] = useState<IAIWritePrecondition>({ title: '', tone: 'FORMAL', wordsLimit: 'unlimited' })
  const [processStatus, setProcessStatus] = useState<'preheat' | 'underway'>('preheat') // AI写作状态，分为“预热”和“进行中
  const [outlines, setOutlines] = useState<IOutline[]>([]) // 大纲
  const [requestStatus, setRequestStatus] = useState<IRequestStatus>({})
  const [dotPositon, setDotPosition] = useState(0)
  const [outlineBoxWidth, setOutlineBoxWidth] = useState(0) // 大纲容器的宽度
  const [allowDrag, setAllowDrag] = useState(false) // 是否允许拖拽

  const stopAIRequestRef = useRef(false) // 是否终止AI请求
  const outlineRefs = useRef(outlines) // outlines的引用，在某些特殊情况下使用，防止outlines的状态无法改变造成的问题
  const requestStatusRef = useRef(requestStatus) // requestStatus的引用，效果同上
  const methodsRef = useRef(methods)
  const { ask, close } = useAiWriter({ dependOnUser: true })

  let timer: any // 共用定时器
  const commonIntervalTime = 10 // 通用定时时间

  let beginTime: any, endTime: any, openTime: any, closeTime: any

  useEffect(() => {
    outlineRefs.current = outlines
  }, [outlines])

  useEffect(() => {
    methodsRef.current = methods
  }, [methods])

  // 初始化前置条件
  const initPrecondition = () => {
    setPrecondition({ title: '', tone: 'FORMAL', wordsLimit: 'unlimited' })
  }

  // 更新请求状态
  const updateRequestStatusRef = (option: IRequestStatus) => {
    requestStatusRef.current = {
      ...requestStatusRef.current,
      ...option,
    }
    setRequestStatus(cloneDeep(requestStatusRef.current))
  }

  // 初始化请求的一些状态
  const initRequestStatus = () => {
    updateRequestStatusRef({
      outlineLoading: false,
      outlineLoaded: false,
      titleLoading: false,
      titleLoaded: false,
      conceptLoaded: false,
      conceptLoading: false,
      referenceLoading: false,
      referenceLoaded: false,
      referenceLoadingPosition: -1,
      analysisLoading: false,
      analysisLoaded: false,
      textLoading: false,
      textLoaded: false,
    })
  }

  // 逐步获取数据
  const getDataByStep = (cacheList: string[]) => {
    let currentIndex = 0
    return {
      next: (isClosed: boolean) => {
        // 如果是流式请求正常关闭并且缓存便利结束 || 退出AI生成
        if ((isClosed && currentIndex >= cacheList.length) || stopAIRequestRef.current) {
          return {
            done: true,
            value: '',
          }
        }

        if (currentIndex >= cacheList.length) {
          return {
            done: false,
            value: '',
          }
        }

        const word = cacheList[currentIndex]
        currentIndex++
        return {
          done: false,
          value: word,
        }
      },
    }
  }

  // 获取大纲标题
  const getOutlineList = () => {
    stopAIRequestRef.current = false
    console.log(chalk.green('开始AI生成大纲标题'))

    beginTime = performance.now()

    return new Promise((resolve) => {
      let currentIndex = 0 // 大纲下标
      let isClosed = false
      const cacheList: string[] = []
      updateRequestStatusRef({
        begin: true,
        outlineLoading: true,
        outlineLoaded: false,
        titleLoading: true,
        titleLoadingPosition: currentIndex,
      })

      const askMsgCallback = (data: DataEvent) => {
        cacheList.push(data.text)
      }

      const askCloseCb_outline = () => {
        closeTime = performance.now()
        chalkConsole('大纲流式接口耗时时间：', formatTime(openTime, closeTime))
        console.log(chalk.blue('结束大纲标题生成'))
        console.log(chalk.bgCyanBright('大纲缓存'), cacheList)
        isClosed = true
      }

      const getByInterval_outline = () => {
        openTime = performance.now()
        chalkConsole('大纲流式接口开启前耗时时间：', formatTime(beginTime, openTime))
        const res = getDataByStep(cacheList)
        timer = setInterval(() => {
          const data = res.next(isClosed)
          const newOutline = outlineRefs.current

          // 临时处理,防止数据量太大
          // if (currentIndex > 2) {
          //   updateRequestStatusRef({
          //     titleLoading: false,
          //     titleLoaded: true,
          //   })
          //   clearInterval(timer)
          //   resolve('')
          //   return
          // }

          if (data.done) {
            updateRequestStatusRef({
              titleLoading: false,
              titleLoaded: true,
            })
            clearInterval(timer)
            resolve('')
            return
          }

          if (!newOutline[currentIndex]) {
            newOutline[currentIndex] = {
              id: uuidV4(),
              title: '',
              concepts: [],
              text: '',
            }
          }

          // 根据换行符，要做一些列状态更新
          if (data.value === '\n') {
            currentIndex++
            requestStatusRef.current.titleLoadingPosition = currentIndex
            setRequestStatus(cloneDeep(requestStatusRef.current))
            return
          }

          newOutline[currentIndex].title += data.value
          setOutlines(cloneDeep(newOutline))
        }, commonIntervalTime)
      }

      ask(
        {
          title: precondition.title,
          tone: precondition.tone as any,
          wordsLimit: precondition.wordsLimit === 'unlimited' ? undefined : `${precondition.wordsLimit}`,
          operateType: OperateType.GENERATE_OUTLINE, // 获取大纲
        },
        {
          openCb: getByInterval_outline,
          msgCb: askMsgCallback,
          closeCb: askCloseCb_outline,
        },
      )
    })
  }

  // 获取写文思路（写作的问题） 批量获取
  const getOutlineConceptByBatch = async () => {
    updateRequestStatusRef({
      conceptLoading: true,
      conceptLoaded: false,
    })

    console.log(chalk.green('开始获取写作问题流程'))
    await bluebird.mapSeries(outlineRefs.current, async (item, idx) => {
      console.log(chalk.bgCyanBright('开始获取写作问题(当前大纲)'), idx + 1)
      // 获取单个大纲的写作问题
      await getConceptBySingleOutline(idx)
    })
    console.log(chalk.blue('结束获取写作问题流程'))

    updateRequestStatusRef({
      conceptLoaded: true,
      conceptLoading: false,
    })
  }

  // 获取单个大纲的写作问题
  const getConceptBySingleOutline = async (outlineIndex: number) => {
    const cacheList: string[] = []
    let isClosed = false

    // 更新当前正在获取写文思路的大纲下标
    updateRequestStatusRef({
      conceptLoading: true,
      conceptLoadingPosition: outlineIndex,
    })

    beginTime = performance.now()

    return new Promise((resolve) => {
      let conceptIndex = 0
      const askMsgCallback = (data: DataEvent) => {
        cacheList.push(data.text)
      }

      const askCloseCb = () => {
        closeTime = performance.now()
        chalkConsole('写作问题接口耗时时间：', formatTime(openTime, closeTime))
        console.log(chalk.bgCyanBright('结束获取写作问题(当前大纲)'), outlineIndex + 1)
        isClosed = true
      }

      const getDataInterval = () => {
        openTime = performance.now()
        chalkConsole('写作问题接口开启前耗时时间：', formatTime(beginTime, openTime))
        const res = getDataByStep(cacheList)
        timer = setInterval(() => {
          const data = res.next(isClosed)
          if (data.done) {
            clearInterval(timer)
            resolve('')
            return
          }

          if (outlineRefs.current.length === 0) {
            // 防止突然退出AI生成，做一次兼容
            return
          }
          const { concepts } = outlineRefs.current[outlineIndex]
          // 根据换行符，要做一些列状态更新
          if (data.value === '\n') {
            conceptIndex++
            // 防止连续出现换行符导致数据处理异常
            concepts[conceptIndex] = {
              id: uuidV4(),
              question: '',
              answerLoading: true,
              answerLoaded: false,
              analysisResult: '',
            }
            return
          }

          if (!concepts[conceptIndex]) {
            concepts[conceptIndex] = {
              id: uuidV4(),
              question: '',
              answerLoading: true,
              answerLoaded: false,
              analysisResult: '',
            }
          }
          concepts[conceptIndex].question += data.value
          setOutlines(cloneDeep(outlineRefs.current))
        }, commonIntervalTime)
      }

      ask(
        {
          title: precondition.title,
          tone: precondition.tone as any,
          wordsLimit: precondition.wordsLimit === 'unlimited' ? undefined : `${precondition.wordsLimit}`,
          outline: outlineRefs.current[outlineIndex].title,
          operateType: OperateType.GENERATE_QUESTION, // 获取生成问题
        },
        {
          openCb: () => getDataInterval(),
          msgCb: askMsgCallback,
          closeCb: askCloseCb,
        },
      )
    })
  }

  // 获取AI流程写作问题的取数（批量获取）
  const getAIConceptSourceDataByBatch = async () => {
    console.log(chalk.green('取数开始'))
    updateRequestStatusRef({
      referenceLoading: true,
      referenceLoaded: false,
    })
    const newOutline = cloneDeep(outlineRefs.current)
    await bluebird.mapSeries(newOutline, async (item, idx) => {
      // 执行单个大纲的取数过程
      await getSourceDataBySingleOutline(idx)
    })

    console.log(chalk.blue('取数完成'))
    // 取数完成，进行下一步
    updateRequestStatusRef({
      referenceLoaded: true,
      referenceLoading: false,
    })
  }

  /**
   * 获取单个大纲的取数
   * @param outlineIndex 当前大纲的下标
   * @param option 配置，其中openCheck表示是否开启检查（特殊处理）
   */
  const getSourceDataBySingleOutline = async (
    outlineIndex: number,
    option?: {
      openCheck: boolean
    },
  ) => {
    // 兼容退出AI生成的情况
    if (outlineRefs.current.length === 0) {
      return
    }

    beginTime = performance.now()

    updateRequestStatusRef({
      referenceLoading: true,
      referenceLoaded: false,
      referenceLoadingPosition: outlineIndex,
    })
    const { concepts } = outlineRefs.current[outlineIndex]

    // 并发处理取数
    const allReq = concepts.map((ele, index) => {
      if (option && option.openCheck) {
        // 既不是新增的，文本也没有被修改过，直接跳过，不需要重新取数
        if (!ele.isNew && !ele.textChanged) {
          console.log('不需要重新取数的问题', index)
          return ''
        }
      }
      return getSourceData(ele.question, outlineIndex, index)
    })
    await Promise.all(allReq)

    endTime = performance.now()
    chalkConsole(`大纲${outlineIndex + 1}取数总耗时时间：`, formatTime(beginTime, endTime))
  }

  /**
   * 获取写作问题的取数
   * @param question 写作问题
   * @param outlineIndex 当前大纲的下标
   * @param conceptIndex 当前写作问题的下标
   * @returns
   */
  const getSourceData = async (question: string, outlineIndex: number, conceptIndex: number) => {
    if (outlineRefs.current.length === 0) {
      // 防止突然退出AI生成，做一次兼容
      return
    }
    outlineRefs.current[outlineIndex].concepts[conceptIndex] = {
      ...outlineRefs.current[outlineIndex].concepts[conceptIndex],
      answerLoaded: false,
      answerLoading: true,
    }
    setOutlines(cloneDeep(outlineRefs.current))
    beginTime = performance.now()

    const res =
      (await getSearchData({
        text: question,
      })) || {}

    if (stopAIRequestRef.current) {
      return
    }

    const { answer = '', reference = [] } = res
    endTime = performance.now()
    chalkConsole(`大纲${outlineIndex + 1}的问题${conceptIndex + 1}取数耗时时间：`, formatTime(beginTime, endTime))
    console.log(chalk.bgCyanBright('获取取数：'), `大纲${outlineIndex + 1}`, conceptIndex, res)
    const newAnswer = answer.replace(/citation:\d+/g, (citation: string) => replaceCitation(citation, reference))

    if (outlineRefs.current.length === 0) {
      // 对退出AI生成过程的情况做兼容
      return
    }
    outlineRefs.current[outlineIndex].concepts[conceptIndex] = {
      ...outlineRefs.current[outlineIndex].concepts[conceptIndex],
      answer: newAnswer,
      reference,
      answerStatus: !!res.answer,
      answerLoaded: true,
      answerLoading: false,
      // 取数后，isNew和textChanged要重新设置为false
      isNew: false,
      textChanged: false,
    }
    setOutlines(cloneDeep(outlineRefs.current))
  }

  // 批量执行分析+段落获取： 当前大纲完成分析后,立马获取段落
  const getAIAnalysisAndParagraphByBatch = async () => {
    await bluebird.mapSeries(outlineRefs.current, async (item, idx) => {
      await getAnalysisBySingleOutline(idx)
    })
  }

  /**
   * 获取单个大纲的数据分析+段落获取
   * @param outlineIndex 当前大纲的下标
   * @param option 配置，其中openCheck表示是否开启检查（特殊处理）
   */
  const getAnalysisBySingleOutline = async (
    outlineIndex: number,
    option?: {
      openCheck: boolean
    },
  ) => {
    updateRequestStatusRef({
      analysisLoading: true,
      analysisLoaded: false,
      analysisLoadingPosition: outlineIndex,
      textLoading: false,
      textLoaded: true,
    })

    // const beginTime = performance.now()

    if (outlineRefs.current.length === 0) {
      // 防止突然退出AI生成，做一次兼容
      return
    }
    // const currentOutline = outlineRefs.current[outlineIndex]
    // .............跳过分析阶段....................................
    // const allAnalysisReq = currentOutline.concepts.map((ele, index) => {
    //   if (option && option.openCheck) {
    //     // 不需要重新分析的直接跳过
    //     if (!ele.needReanalysis) {
    //       return ''
    //     }
    //   }
    //   return getAnalysis(index, outlineIndex)
    // })

    // 并发执行分析
    // await Promise.all(allAnalysisReq)

    await sleep(200) // 模拟分析
    // const endTime = performance.now()
    // console.log(chalk.bgRedBright(`大纲${outlineIndex + 1}分析总耗时时间：`), formatTime(beginTime, endTime))

    // 执行完分析后,获取段落文本
    await getAIParagraph(outlineIndex)
  }

  /**
   * 获取分析
   * @param conceptIndex 写作问题的下标
   * @param outlineIndex 当前所处大纲的下标
   * @returns
   */
  const getAnalysis = async (conceptIndex: number, outlineIndex: number) => {
    if (outlineRefs.current.length === 0) {
      // 防止突然退出AI生成，做一次兼容
      return
    }
    const currentOutline = outlineRefs.current[outlineIndex]
    const currentConcept = currentOutline.concepts[conceptIndex]

    beginTime = performance.now()

    return new Promise((resolve) => {
      const cacheList: string[] = []
      let isClosed = false
      let timer: any // 涉及到并发，不能用共用的定时器，需要内部设置自己的定时器
      console.log(chalk.bgCyanBright(`大纲${outlineIndex + 1}分析中...`), `问题${conceptIndex + 1}`)
      const askMsgCallback = (data: DataEvent) => {
        cacheList.push(data.text)
      }

      const askCloseCb = () => {
        console.log(chalk.bgYellowBright(`问题${conceptIndex + 1}数据分析接口关闭`), `大纲${outlineIndex + 1}`, cacheList)
        isClosed = true

        const currentOutline = outlineRefs.current[outlineIndex]
        if (!currentOutline.concepts[conceptIndex]?.analysisResult) {
          currentOutline.concepts[conceptIndex].analysisResult = ''
        }
        currentOutline.concepts[conceptIndex].analysisResult += cacheList.join('')
        setOutlines(cloneDeep(outlineRefs.current))

        closeTime = performance.now()
        chalkConsole(`问题${conceptIndex + 1}分析耗时时间：`, formatTime(openTime, closeTime))
        resolve('')
      }

      const getByInterval = () => {
        openTime = performance.now()
        chalkConsole(`问题${conceptIndex + 1}接口开启前耗时时间：`, formatTime(beginTime, openTime))
        const res = getDataByStep(cacheList)
        // timer = setInterval(() => {
        //   const data = res.next(isClosed)
        //   if (data.done) {
        //     console.log(chalk.bgCyanBright('数据分析结果遍历完毕'), outlineIndex)
        //     clearInterval(timer)
        //     resolve('')
        //     return
        //   }

        //   if (outlineRefs.current.length === 0) {
        //     // 防止突然退出AI生成，做一次兼容
        //     return
        //   }
        //   const currentOutline = outlineRefs.current[outlineIndex]
        //   if (!currentOutline.concepts[conceptIndex]?.analysisResult) {
        //     currentOutline.concepts[conceptIndex].analysisResult = ''
        //   }
        //   currentOutline.concepts[conceptIndex].analysisResult += data.value
        //   setOutlines(cloneDeep(outlineRefs.current))
        // }, commonIntervalTime)
      }

      ask(
        {
          title: precondition.title,
          tone: precondition.tone as any,
          wordsLimit: precondition.wordsLimit === 'unlimited' ? undefined : `${precondition.wordsLimit}`,
          operateType: OperateType.ANALYSIS_AND_PROCESS, // 分析和加工
          question: currentConcept.question,
          data: currentConcept.answer,
        },
        {
          openCb: getByInterval,
          msgCb: askMsgCallback,
          closeCb: askCloseCb,
        },
      )
    })
  }

  // 获取段落
  const getAIParagraph = async (outlineIndex: number) => {
    // 更新当前正在获取写文思路的大纲下标
    updateRequestStatusRef({
      textLoading: true,
      textLoadingPosition: outlineIndex,
      analysisLoaded: true,
      analysisLoading: false,
    })
    if (outlineRefs.current.length === 0) {
      // 防止突然退出AI生成，做一次兼容
      return
    }
    const currentOutline = outlineRefs.current[outlineIndex]
    currentOutline.text = '' // 每次获取段落，先置空
    console.log(chalk.green(`大纲${outlineIndex + 1}获取段落中`))

    beginTime = performance.now()

    // 获取段落，需要整合所有的分析结果作为参数传递过去
    // let allAnalysisText = ''
    // currentOutline.concepts.forEach((ele, index) => {
    //   if (index === 0) {
    //     allAnalysisText += ele.analysisResult
    //   } else {
    //     allAnalysisText += `\n${ele.analysisResult}`
    //   }
    // })

    let allQuestion = '' // 所有的问题
    let allData = '' // 所有问题的取数

    currentOutline.concepts.forEach((ele, index) => {
      if (index === 0) {
        allQuestion += ele.question
        allData += ele.answer
      } else {
        allQuestion += `\n${ele.question}`
        allData += `\n${ele.answer}`
      }
    })

    const temp = {
      allQuestion,
      allData,
    }

    console.log('请求段落的文本数据', temp)

    return new Promise((resolve) => {
      if (outlineRefs.current.length === 0) {
        // 防止突然退出AI生成，做一次兼容
        return
      }
      const currentOutline = outlineRefs.current[outlineIndex]
      const cacheList: any = []
      let isClosed = false

      const askMsgCallback = (data: DataEvent) => {
        cacheList.push(data.text)
      }

      const askCloseCb = () => {
        closeTime = performance.now()
        chalkConsole(`大纲${outlineIndex + 1}的段落流式接口获取耗时时间：`, formatTime(openTime, closeTime))
        console.log(chalk.bgYellowBright('获取段落接口关闭'), outlineIndex)
        isClosed = true
      }

      const getDataInterval = () => {
        openTime = performance.now()
        chalkConsole(`大纲${outlineIndex + 1}的段落流式接口开启前耗时时间：`, formatTime(beginTime, openTime))
        const res = getDataByStep(cacheList)
        timer = setInterval(() => {
          const data = res.next(isClosed)
          if (data.done) {
            console.log(chalk.bgCyanBright('段落文本遍历结束'), outlineIndex)
            clearInterval(timer)
            resolve('')
            return
          }
          if (outlineRefs.current.length === 0) {
            // 防止突然退出AI生成，做一次兼容
            return
          }
          const currentOutline = outlineRefs.current[outlineIndex]
          currentOutline.text += data.value
          setOutlines(cloneDeep(outlineRefs.current))
        }, commonIntervalTime)
      }

      ask(
        {
          title: precondition.title,
          tone: precondition.tone as any,
          wordsLimit: precondition.wordsLimit === 'unlimited' ? undefined : `${precondition.wordsLimit}`,
          outline: currentOutline.title || '',
          operateType: OperateType.PARAGRAPH_GENERATE, // 段落生成
          // text: allAnalysisText, // 分析结果文本
          question: allQuestion,
          data: allData,
        },
        {
          openCb: getDataInterval,
          msgCb: askMsgCallback,
          closeCb: askCloseCb,
        },
      )
    })
  }

  // 编辑大纲标题后的重新生成
  const generateNewOutline = async (outlineIndex: number) => {
    handleAIWritingStatus(true)
    updateRequestStatusRef({
      outlineLoading: true,
      outlineLoaded: false,
      conceptLoaded: false,
      conceptLoading: true,
    })
    // 生成新的问题
    await getConceptBySingleOutline(outlineIndex)
    updateRequestStatusRef({
      conceptLoaded: true,
      conceptLoading: false,
    })

    // 新的问题再取数
    await getSourceDataBySingleOutline(outlineIndex)
    updateRequestStatusRef({
      referenceLoading: false,
      referenceLoaded: true,
    })
    // 分析数据+生成文本
    await getAnalysisBySingleOutline(outlineIndex)
    updateRequestStatusRef({
      outlineLoading: false,
      outlineLoaded: true,
      textLoaded: true,
      textLoading: false,
    })
  }

  // 编辑（或新增）某个大纲下面的写作问题后重新生成
  const generateNewConceptReference = async (outlineIndex: number) => {
    handleAIWritingStatus(true)
    updateRequestStatusRef({
      outlineLoading: true,
      outlineLoaded: false,
    })

    // 是否需要重新取数
    const needRetrievedata = outlineRefs.current[outlineIndex].concepts.some((ele) => {
      return ele.textChanged
    })

    if (needRetrievedata) {
      // 被编辑的问题的取数
      await getSourceDataBySingleOutline(outlineIndex, { openCheck: true })
      updateRequestStatusRef({
        referenceLoading: false,
        referenceLoaded: true,
        // referenceLoadingPosition: outlineIndex
      })
      // 取数完成后再把更改的（或者新增的问题）问题进行分析，然后再获取段落
      await getAnalysisBySingleOutline(outlineIndex, { openCheck: true })
    } else {
      await getAIParagraph(outlineIndex)
    }

    updateRequestStatusRef({
      outlineLoading: false,
      outlineLoaded: true,
      textLoaded: true,
      textLoading: false,
      textLoadingPosition: -1,
    })
  }

  // 生成最后的文章
  const generateArticle = async () => {
    // 先单独保存一份，因为当跳转id后，outlines会被清空
    const newOutlines = cloneDeep(outlineRefs.current)
    console.log(chalk.green('生成文章'))
    const articleId = await createDraft()
    EE.emit('updatePages') // 更新文章列表
    history.push(`/writer/${articleId}`)
    setInAIOutlineProcess(false)

    beginTime = performance.now()

    return new Promise((resolve) => {
      let isClosed = false
      let paragraphText = ''
      let resultText = ''
      const cacheList: string[] = []
      let firstLineBreak = true // 是否是第一个换行符（第一个换行符后面的都是正文）
      let title = '' // 标题

      newOutlines.forEach((ele, index) => {
        if (index === 0) {
          paragraphText += ele.text
        } else {
          paragraphText += `\n${ele.text}`
        }
      })

      const askMsgCallback_article = (data: DataEvent) => {
        cacheList.push(data.text)
      }

      const askCloseCb = () => {
        closeTime = performance.now()
        chalkConsole('文章流式接口耗时时间：', formatTime(openTime, closeTime))
        console.log(chalk.blue('生成文章结束'))
        console.log(chalk.bgCyanBright('文章缓存...'), cacheList)
        isClosed = true
      }

      const getByInterval_article = () => {
        openTime = performance.now()
        chalkConsole('文章流式接口开启前耗时时间：', formatTime(beginTime, openTime))
        const res = getDataByStep(cacheList)
        let cacheIndex = 0
        timer = setInterval(() => {
          const data = res.next(isClosed)
          if (data.done) {
            console.log(chalk.blue('文章生成结束'), resultText)
            clearInterval(timer)
            // 不再处于AI写作状态
            handleAIWritingStatus(false)
            // 写入完毕，触发保存
            EE.emit('savePage')
            // 初始化DOM监听
            handleInitStatus(true)
            resolve('')
            return
          }

          if (data.value === '\n' && firstLineBreak) {
            firstLineBreak = false
            // 第一个换行符丢弃
            return
          }

          // 处理标题
          if (firstLineBreak) {
            // 对返回的文本做兼容，手动去除[Title:]
            if (cacheIndex > 1 && title.startsWith('Title:')) {
              title = ''
            }

            title += data.value
            cacheIndex++

            if (cacheIndex > 1) {
              handlePageInfo({
                id: articleId,
                title,
              })
            }
            return
          }

          resultText += data.value
          methodsRef.current?.insertNewNodes(resultText)
        }, commonIntervalTime)
      }

      ask(
        {
          title: precondition.title,
          tone: precondition.tone as any,
          wordsLimit: precondition.wordsLimit === 'unlimited' ? undefined : `${precondition.wordsLimit}`,
          operateType: OperateType.ARTICLE_GENERATE, // 文章生成
          text: paragraphText, // 所有大纲段落的拼接
        },
        {
          openCb: getByInterval_article,
          msgCb: askMsgCallback_article,
          closeCb: askCloseCb,
        },
      )
    })
  }

  // 停止AI大纲写作阶段的所有生成
  const stopAIGenerate = () => {
    clearInterval(timer)
    close() // 关闭流式请求
    initPrecondition()
    handleAIWritingStatus(false) // 退出AI写作状态
    stopAIRequestRef.current = true // 中止请求
    outlineRefs.current = [] // 提前清空数据
    // 退回到预热状态，要清空数据、关闭请求
    setProcessStatus('preheat')
    setOutlines([])
    initRequestStatus()
  }

  // 初始化AI大纲的整体生成流程
  const initAIGenerateProcess = async () => {
    // 获取大纲
    handleAIWritingStatus(true)
    await getOutlineList()
    // 获取大纲问题
    await getOutlineConceptByBatch()
    // 取数
    await getAIConceptSourceDataByBatch()
    // 分析+获取段落
    await getAIAnalysisAndParagraphByBatch()
    updateRequestStatusRef({
      outlineLoading: false,
      outlineLoaded: true,
      textLoaded: true,
      textLoading: false,
    })
  }

  const handleAIWritingStatus = (status: boolean) => {
    console.log(chalk.green('isAIWriting状态'), status)
    setIsAIWriting(status)
  }

  return {
    precondition,
    setPrecondition,
    processStatus,
    setProcessStatus,
    outlines,
    setOutlines,
    isAIWriting,
    setIsAIWriting,
    inAIOutlineProcess,
    setInAIOutlineProcess,

    requestStatus,
    setRequestStatus,
    dotPositon,
    setDotPosition,
    outlineBoxWidth,
    setOutlineBoxWidth,
    allowDrag,
    setAllowDrag,

    initPrecondition,
    outlineRefs,
    requestStatusRef,
    stopAIRequestRef,
    updateRequestStatusRef,
    getOutlineList,
    getOutlineConceptByBatch,
    getAIConceptSourceDataByBatch,
    getAIAnalysisAndParagraphByBatch,
    generateArticle,
    generateNewConceptReference,
    generateNewOutline,
    stopAIGenerate,
    initAIGenerateProcess,
    handleAIWritingStatus,
    initRequestStatus,
  }
}

export default useAiOutline
